import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import Layout from "app/components/layouts";
import OtpInput from "react-otp-input";
import { maskPhoneNumber } from "app/utils/index";
import MainTitle from "app/components/common/typography/MainTitle";
import SubTitle from "app/components/common/typography/SubTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import toast from "react-hot-toast";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useVerifyOtp } from "app/hooks/applicant/useVerifyOtp";
import { useResendOtp } from "app/hooks/applicant/useResendOtp";
import { handleApiError } from "app/utils/handleApiError";

import {
  extractSavedIds,
  extractVehicleDetails,
  extractLoanApplicationData,
  extractApplicantData,
  handleStepNavigation,
  jointExtractApplicantData,
  JointApplicationStepNavigation,
} from "app/utils/preSavedDataHelperFunctions";

import {
  addStepsData,
  setSavedIds,
  addJointApplicationData,
} from "app/lib/store/slices/steps/loanTypesSteps";

interface FormData {
  otp: string;
}

const Otp: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const [timer, setTimer] = useState<number>(0);
  const [isTimerVisible, setIsTimerVisible] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { mutate: ResendOTPMutate, isPending: resedisPending } = useResendOtp();
  const { mutate, isPending } = useVerifyOtp();

  const phoneNumber = location.state?.phoneNumber;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      otp: "",
    },
  });
  const onSubmit = (data: FormData) => {
    const { otp } = data;
    if (id) {
      mutate(
        { id: id, otp: parseInt(otp) },
        {
          onSuccess: (response: any) => {
            if (!response) return;
            if (
              response?.loanApplication &&
              response?.loanApplication?.isCompleted
            ) {
              toast.success("Application is already completed");
              reset({ otp: "" });
              return;
            } else {
              toast.success("Verified Successfully");
            }
            const { loanApplication } = response;
            const { applicants, vehicleDetail, applicationType, step } =
              loanApplication || {};

            // saved the ids

            const savedIds = extractSavedIds(
              applicants,
              vehicleDetail,
              loanApplication,
              applicationType,
              loanApplication?.applicationReferenceNumber ?? ""
            );

            if (Object.keys(savedIds).length > 0)
              dispatch(setSavedIds(savedIds));

            // End saved the ids

            // store the Applicant data

            if (applicationType !== "joint") {
              dispatch(
                addStepsData({
                  vehicleDetail: extractVehicleDetails(vehicleDetail),
                  loanApplication: extractLoanApplicationData(loanApplication),
                  applicants: extractApplicantData(applicants),
                })
              );
            } else {
              dispatch(
                addStepsData({
                  vehicleDetail: extractVehicleDetails(vehicleDetail),
                  loanApplication: extractLoanApplicationData(loanApplication),
                })
              );
              const { yourDetails, Coborrower } =
                jointExtractApplicantData(applicants);
              dispatch(
                addJointApplicationData({
                  category: "YourDetails",
                  data: yourDetails,
                })
              );

              dispatch(
                addJointApplicationData({
                  category: "Coborrower",
                  data: Coborrower,
                })
              );
            }

            // Handle Step Navigation

            if (applicationType !== "joint") {
              const employment = applicants[0]?.employment || {};
              const incomesData = applicants[0]?.incomes || [];
              // const assetLiabilityData = applicants[0]?.assetLiability || {};

              handleStepNavigation(
                step,
                applicants,
                employment,
                incomesData,
                dispatch,
                navigate
              );
            } else {
              const sortedApplicants = [
                ...applicants?.filter((a: any) => a.isPrimary),
                ...applicants?.filter((a: any) => !a.isPrimary),
              ];
              const mainApplicant = sortedApplicants?.[0];
              const childApplicant = sortedApplicants?.[1];

              const applicantOne = mainApplicant;
              const applicantTwo = childApplicant;

              const employmentOne = mainApplicant?.employment;
              const employmentTwo = childApplicant?.employment;

              const incomesDataOne = mainApplicant?.incomes;
              const incomesDataTwo = childApplicant?.incomes;

              JointApplicationStepNavigation(
                step,
                applicantOne,
                applicantTwo,
                employmentOne,
                employmentTwo,
                incomesDataOne,
                incomesDataTwo,
                dispatch,
                navigate
              );
            }

            reset({ otp: "" });
          },
          onError: (err: any) => {
            const message =
              err?.response?.data?.error?.toString() ??
              err?.response?.data?.message?.toString() ??
              err?.message?.toString();
            if (message === "Invalid OTP") {
              toast.error("Please enter correct OTP");
            } else {
              handleApiError(err);
            }
          },
        }
      );
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isTimerVisible && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isTimerVisible, timer]);

  const handleResend = () => {
    if (id && timer === 0) {
      ResendOTPMutate(
        { id },
        {
          onSuccess: () => {
            toast.success("OTP has been resent successfully");
            setTimer(30);
            setIsTimerVisible(true);
          },
          onError: (err: any) => {
            handleApiError(err);
          },
        }
      );
    }
  };

  return (
    <>
      <Layout>
        <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0 mt-10">
          <MainTitle title="OTP" />
          <SubTitle
            title={`Please enter the code we have sent to ${maskPhoneNumber(
              phoneNumber
            )}`}
          />
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex justify-center ">
              <Controller
                name="otp"
                control={control}
                rules={{
                  required: "OTP is required",
                  minLength: { value: 4, message: "OTP must be 4 digits" },
                }}
                render={({ field }) => (
                  <OtpInput
                    {...field}
                    value={field.value}
                    onChange={field.onChange}
                    numInputs={4}
                    inputStyle={{
                      width: "75px",
                      height: "65px",
                      margin: "0 7px",
                      borderRadius: "8px",
                      border: `${
                        errors.otp ? "1px solid red" : "1px solid #D9D9D9"
                      }`,
                      textAlign: "center",
                      fontSize: "18px",
                      backgroundColor: "#fff",
                    }}
                    renderInput={(props) => <input {...props} />}
                  />
                )}
              />
            </div>
            <div className="mt-4 sm:mt-5">
              <button
                type="button"
                className={`text-base font-Mulish font-semibold mt-4 ${
                  isPending || resedisPending || timer > 0
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gold"
                }`}
                disabled={isPending || resedisPending || timer > 0}
                onClick={handleResend}
              >
                {resedisPending ? "Loading..." : "Resend OTP"}
              </button>

              {isTimerVisible && timer > 0 && (
                <p className="text-gold text-[16px] mt-2">
                  00:{timer.toString().padStart(2, "0")}
                </p>
              )}
              {errors.otp && <ErrorsMessage title={errors.otp?.message} />}
            </div>
            <div className="flex justify-between my-10">
              <ContinueButton
                className="sm:!w-[70%] w-full  mx-auto"
                type="submit"
                loading={isPending}
              >
                Continue
              </ContinueButton>
            </div>
          </form>
        </div>
      </Layout>
    </>
  );
};

export default Otp;
